import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Stepper } from "@/components/ui/stepper";
import { InfoBanner } from "@/components/ui/banner";
import { PermissionDeniedState } from "@/components/ui/system-state";
import { Modal } from "@/components/ui/modal";
import { Field, Input } from "@/components/ui/input";
import { toast } from "@/components/ui/toast";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { matchesAllTerms } from "@/components/data-display/filter-bar";
import { cn } from "@/lib/utils";
import { roleDisplayLabel } from "@/lib/permissions";
import { isMachineNamedWallet } from "@/lib/wallet-display-name";
import { usePermission } from "@/hooks/use-permission";
import { useTransferData, type WalletLookupFailure, type WalletLookupOutcome } from "@/hooks/use-transfer-data";
import { WALLET_ID_PATTERN } from "@/lib/api";
import { useErrorLocale } from "@/i18n/use-error-locale";
import type { TransferDestinationTab, TransferRequestValues, TransferWalletOption } from "@/types";
import type { TransferWizardStep } from "@/features/transfers/transfer-state";

const WIZARD_STEPS = ["Source", "Destination", "Asset", "Amount", "Review"].map((label) => ({ label }));

/**
 * The destination tab strip. "Saved Recipient" has no backing capability: no
 * saved-recipient API, route, fixture or store exists anywhere in the gateway or
 * this app (checked: `grep -rn "recipient" apps/sandbox-shell/src` returns only
 * policy copy). It used to render an unconditional "No saved recipients yet."
 * panel, which presents absence-of-capability as an empty state of a working
 * feature and implies a data source that does not exist. It now renders as a
 * disabled tab carrying the same honest "available once the linked module is
 * built" affordance the transfer detail page already uses for unbuilt modules
 * (`CROSS_MODULE_HINT` in transfer-detail.tsx), so the control is truthful and
 * the wizard can never treat the tab as a selectable destination path.
 */
const DEST_TABS: { key: TransferDestinationTab; label: string; unavailableHint?: string }[] = [
  { key: "internal", label: "Internal Wallet" },
  { key: "saved", label: "Saved Recipient", unavailableHint: "Available once the linked module is built" },
  { key: "external", label: "External Address" },
];

/**
 * Catalog role that grants `transfer:create`. A denied viewer is told the role
 * they need by its display label ("Wallet Operator"), never the internal
 * permission key (`transfer.write`), so the denial is actionable without
 * exposing the permission model. Mirrors the PR #332 pattern
 * (`features/wallets/wallet-request.tsx`) and the wallet-operations spec, which
 * authorizes the Wallet operator to create eligible transfer intents.
 */
const TRANSFER_CREATE_ROLE = "wallet_operator";

const EMPTY_VALUES: TransferRequestValues = {
  sourceWalletId: "",
  destTab: "internal",
  destWalletId: "",
  externalAddress: "",
  asset: "",
  amount: "",
  memo: "",
  clientRef: "",
};

/**
 * UX audit D03 (ALL-5825): the source-wallet picker carries a search box, an honest
 * "no wallets match" line, and a toggle that hides the lab/machine-named wallets the
 * wallets list already hides by default (ALL-5795, `isMachineNamedWallet`). This is
 * presentation only: the loaded list, the selection and the submit path are unchanged,
 * and hiding lab noise is a user-visible toggle — it never alters the data.
 */
function walletOptionMeta(w: TransferWalletOption): string {
  // The gateway's provider label ("Mesh (Silence Trio 2-of-3)") is preferred over the
  // raw provider-route id ("silence-trio-2of3-sepolia") the hook folds into `meta`.
  // Fall back to `meta` when the gateway sent no provider, so nothing is invented.
  if (w.provider) return [w.chain, w.network, w.provider].filter(Boolean).join(" · ");
  return w.meta;
}

/** " (REASON_CODE, ref SR-1)" for a failed by-id lookup, or "" when none is known. */
function lookupFailureText(failure: WalletLookupFailure | undefined): string {
  if (!failure) return "";
  return ` (${failure.reasonCode}${failure.supportReference ? `, ref ${failure.supportReference}` : ""})`;
}

function WalletCard({
  walletId,
  name,
  meta,
  icon,
  selected,
  onClick,
  disabled,
  frozen,
}: {
  /** Set on source cards only, so the lab and tests can pick a source wallet by its exact id. */
  walletId?: string;
  name: string;
  meta: string;
  icon: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  frozen?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      data-wallet-id={walletId}
      className={cn(
        "flex w-full items-center gap-3.5 rounded-lg border bg-surface px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
        selected ? "border-tint-info-border bg-tint-info" : "border-line hover:border-primary",
        disabled && "cursor-not-allowed opacity-85 hover:border-line"
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-info-soft text-[15px] font-bold text-info">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-bold text-ink">
          {name}
          {frozen && (
            <span className="rounded-[4px] bg-danger-soft px-1.5 py-0.5 text-[11px] font-bold text-danger">{frozen}</span>
          )}
        </span>
        <span className="mt-0.5 block text-[13px] text-muted">{meta}</span>
      </span>
      {selected && (
        <svg
          aria-hidden
          className="ml-auto size-[18px] shrink-0 text-primary"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 10.5l3.5 3.5L15 6" />
        </svg>
      )}
    </button>
  );
}

function ReviewRow({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-5 border-b border-line py-[11px] text-sm last:border-b-0">
      <span className="font-medium text-muted">{k}</span>
      <span className={cn("max-w-[60%] text-right font-semibold text-ink", mono && "font-mono text-[13px]")}>{v}</span>
    </div>
  );
}


export default function TransferRequestFlow({ initialStep }: { initialStep: TransferWizardStep }) {
  const navigate = useNavigate();
  const canCreate = usePermission("transfer:create");
  const transferData = useTransferData();
  const [searchParams] = useSearchParams();
  const errorCatalog = useErrorLocale(searchParams);
  const [step, setStep] = React.useState<TransferWizardStep>(initialStep);
  const [values, setValues] = React.useState<TransferRequestValues>({ ...EMPTY_VALUES });
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitErrorSupport, setSubmitErrorSupport] = React.useState<{ supportReference?: string; capability?: string }>({});

  const set = <K extends keyof TransferRequestValues>(key: K, value: TransferRequestValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const sourceWallets = transferData.sourceWallets;
  const destWalletsAll = transferData.destWallets;
  const assets = transferData.assets;

  // UX audit D03: search and lab-wallet visibility for the source picker. Both are
  // view state — `sourceWallets` is never mutated, so later steps see the full list.
  const [walletQuery, setWalletQuery] = React.useState("");
  const [showLabWallets, setShowLabWallets] = React.useState(false);

  // ALL-6339: a wallet the loaded pages do not include is reached by id, not by paging: a deep
  // link (`/transfers/new?source=<walletId>`, the wallet list's "New transfer") or a wallet id
  // typed into the search. Both read `GET /v1/walletkit/wallets/{id}` and add that one wallet.
  const requestedSourceId = React.useMemo(() => {
    const raw = (searchParams.get("source") ?? "").trim();
    return WALLET_ID_PATTERN.test(raw) ? raw : null;
  }, [searchParams]);
  // "idle" until the deep-linked wallet has been looked up (or there is none), so the default
  // auto-selection below never races it.
  const [requestedSourceState, setRequestedSourceState] = React.useState<"idle" | "looking" | WalletLookupOutcome>(
    requestedSourceId ? "looking" : "idle",
  );
  const [idLookup, setIdLookup] = React.useState<{ id: string; state: "looking" | WalletLookupOutcome } | null>(null);

  const { shownSourceWallets, hiddenWalletCount } = React.useMemo(() => {
    let hidden = 0;
    const shown: TransferWalletOption[] = [];
    for (const w of sourceWallets) {
      const machineNamed = isMachineNamedWallet({ name: w.name, storedAlias: w.storedAlias, walletId: w.id });
      if (machineNamed) hidden += 1;
      // The selected wallet is always shown: a source the person cannot see must never be the
      // one a transfer moves funds from (a deep-linked lab wallet is machine-named).
      if (machineNamed && !showLabWallets && w.id !== values.sourceWalletId) continue;
      if (matchesAllTerms(walletQuery, [w.name, w.address, w.chain, w.meta, w.id])) shown.push(w);
    }
    return { shownSourceWallets: shown, hiddenWalletCount: hidden };
  }, [sourceWallets, showLabWallets, walletQuery, values.sourceWalletId]);

  const typedLookupSeqRef = React.useRef(0);
  // Counts every change of the chosen source, so a late answer can tell "unchanged" from a
  // choice that went A, then B, then back to A while the lookup ran.
  const sourceRevisionRef = React.useRef(0);
  const lastSourceIdRef = React.useRef(values.sourceWalletId);
  if (lastSourceIdRef.current !== values.sourceWalletId) {
    lastSourceIdRef.current = values.sourceWalletId;
    sourceRevisionRef.current += 1;
  }

  const selectSource = React.useCallback((walletId: string) => {
    setValues((v) => {
      const nextDests = transferData.destWallets.filter((d) => d.id !== walletId);
      return {
        ...v,
        sourceWalletId: walletId,
        destWalletId: nextDests.some((d) => d.id === v.destWalletId) ? v.destWalletId : (nextDests[0]?.id ?? ""),
      };
    });
  }, [transferData.destWallets]);

  // Look up the deep-linked source once the first page has loaded, then select it.
  const { findWalletById } = transferData;
  React.useEffect(() => {
    if (!requestedSourceId || requestedSourceState !== "looking") return;
    if (transferData.walletsLoading) return;
    if (transferData.walletsError) { setRequestedSourceState("error"); return; }
    let cancelled = false;
    void findWalletById(requestedSourceId).then((outcome) => {
      if (!cancelled) setRequestedSourceState(outcome);
    });
    return () => { cancelled = true; };
  }, [requestedSourceId, requestedSourceState, transferData.walletsLoading, transferData.walletsError, findWalletById]);

  React.useEffect(() => {
    if (requestedSourceState !== "found" || !requestedSourceId) return;
    const wallet = sourceWallets.find((w) => w.id === requestedSourceId);
    // A card the person chose while the lookup ran is their choice and stands; the deep link only
    // fills an empty selection (auto-selection is held while the lookup runs, so it is empty).
    if (wallet && !wallet.disabled && !values.sourceWalletId) {
      setValues((v) => (v.sourceWalletId ? v : { ...v, sourceWalletId: requestedSourceId }));
    }
    // A linked wallet that is frozen cannot be the source: fail closed like a wallet that was not
    // found (no default is preselected in its place). Otherwise the link is applied once and
    // after this the person's own choice stands.
    if (wallet) setRequestedSourceState(wallet.disabled ? "not_found" : "idle");
  }, [requestedSourceState, requestedSourceId, sourceWallets, values.sourceWalletId]);

  // Offered only for a query that looks like an id ("wlt_…", or a long token with a digit), never
  // for an address or a plain word, so ordinary searches are unchanged.
  const typedWalletId = React.useMemo(() => {
    const q = walletQuery.trim();
    if (!WALLET_ID_PATTERN.test(q) || /^0x/i.test(q)) return null;
    return /^wlt_/i.test(q) || (q.length >= 8 && /\d/.test(q)) ? q : null;
  }, [walletQuery]);
  const typedWalletLoaded = typedWalletId ? sourceWallets.some((w) => w.id === typedWalletId) : false;
  const lookUpTypedWallet = React.useCallback(() => {
    if (!typedWalletId) return;
    const id = typedWalletId;
    // Overlapping lookups: only the latest one may select, and only if the person has not changed
    // the source since asking (not even A, B, A) (response order must never decide the source of a transfer).
    const seq = ++typedLookupSeqRef.current;
    const revisionAtClick = sourceRevisionRef.current;
    setIdLookup({ id, state: "looking" });
    void findWalletById(id).then((outcome) => {
      if (seq !== typedLookupSeqRef.current) return;
      setIdLookup((current) => (current && current.id === id ? { id, state: outcome } : current));
      if (outcome === "found" && sourceRevisionRef.current === revisionAtClick) selectSource(id);
    });
  }, [typedWalletId, findWalletById, selectSource]);

  // Auto-select the first wallet once the real wallet list loads. A source the
  // person cannot see must never be preselected for a funds movement, so the
  // choice is the first wallet the picker shows by default (not machine-named).
  // When every wallet is machine-named nothing is preselected: the person turns
  // on "Show test and lab wallets" and picks one deliberately.
  React.useEffect(() => {
    // Fail closed (ALL-6339): while a deep-linked source is being looked up, and after that lookup
    // failed or found nothing sendable, no other wallet is preselected for a funds movement; the
    // person picks one deliberately (the banner says so).
    if (requestedSourceId && requestedSourceState !== "idle") return;
    if (!values.sourceWalletId && sourceWallets.length > 0) {
      const named = sourceWallets.find(
        (w) => !isMachineNamedWallet({ name: w.name, storedAlias: w.storedAlias, walletId: w.id }),
      );
      if (named) setValues(v => ({ ...v, sourceWalletId: named.id }));
    }
  }, [sourceWallets, values.sourceWalletId, requestedSourceState, requestedSourceId]);

  // Load real balances/assets for the selected wallet.
  React.useEffect(() => {
    if (values.sourceWalletId) {
      transferData.loadAssetsForWallet(values.sourceWalletId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.sourceWalletId]);

  // Auto-select the first asset once real balances load.
  React.useEffect(() => {
    if (assets.length > 0 && !assets.find(a => a.symbol === values.asset)) {
      setValues(v => ({ ...v, asset: assets[0].symbol }));
    }
  }, [assets, values.asset]);

  const source = sourceWallets.find((w) => w.id === values.sourceWalletId);
  const destWallets = destWalletsAll.filter((w) => w.id !== values.sourceWalletId);
  const destWallet = destWallets.find((w) => w.id === values.destWalletId);
  const asset = assets.find((a) => a.symbol === values.asset);
  const amountNum = parseFloat(values.amount.replace(/,/g, "")) || 0;

  const destinationLabel =
    values.destTab === "external"
      ? values.externalAddress || "External address"
      : `${destWallet?.name ?? "Internal wallet"} (${destWallet?.id ?? "—"})`;

  const destinationInput = React.useMemo(() => {
    // The asset row's network is a display label (for example, "ethereum sepolia").
    // Transfer scope is owned by the selected source wallet and must use its canonical
    // network id (for example, "sepolia"); never submit the display label.
    const network = source?.network ?? "";
    if (values.destTab === "external") {
      return { type: "external" as const, address: values.externalAddress, network };
    }
    return { type: "internal" as const, walletId: destWallet?.id ?? "", network };
  }, [values.destTab, values.externalAddress, destWallet?.id, source?.network]);

  // Load the fee/policy estimate when entering the review step.
  React.useEffect(() => {
    if (step === 5 && values.sourceWalletId && values.asset && amountNum > 0) {
      transferData.loadEstimate({
        sourceWalletId: values.sourceWalletId,
        destination: destinationInput,
        asset: values.asset,
        amount: values.amount.replace(/,/g, ""),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const canContinue =
    step === 1
      ? Boolean(source)
      : step === 2
        ? values.destTab === "external"
          ? values.externalAddress.trim().length > 0
          : Boolean(destWallet)
        : step === 3
          ? Boolean(asset)
          : step === 4
            ? amountNum > 0
            : true;

  const go = (n: TransferWizardStep) => setStep(n);
  const next = () => setStep((s) => (s < 5 ? ((s + 1) as TransferWizardStep) : s));

  const submit = async () => {
    if (!canCreate || submitting || !source || !asset) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorSupport({});

    const result = await transferData.submitTransfer({
      sourceWalletId: values.sourceWalletId,
      destination: destinationInput,
      asset: values.asset,
      amount: values.amount.replace(/,/g, ""),
    });
    setSubmitting(false);
    setConfirmOpen(false);
    if (result.ok && result.transferId) {
      toast({
        variant: "success",
        title: "Transfer submitted for approval",
        description: `${values.amount} ${values.asset} · ${destinationLabel}`,
        supportReference: result.supportReference,
      });
      // Submission created a real backend record — send the user to the
      // transfer detail page for that real id, which reads live API data.
      // Never fabricate a local record for it (R5/R11).
      navigate(`/transfers/${result.transferId}`);
    } else {
      setSubmitError(result.error || "Could not submit transfer. Please try again.");
      setSubmitErrorSupport({ supportReference: result.supportReference, capability: result.capability });
      toast({
        variant: "error",
        title: "Transfer failed",
        description: result.error || "Could not submit transfer. Please try again.",
        supportReference: result.supportReference,
        capability: result.capability,
      });
    }
  };

  return (
    <div className="max-w-[900px]">
      <Breadcrumb items={[{ label: "Transfers", href: "/transfers" }, { label: "Create Intent" }]} />
      {!canCreate && (
        <div className="mb-6">
          <PermissionDeniedState
            title="You can't create transfers"
            description={`Creating a transfer needs the ${roleDisplayLabel(TRANSFER_CREATE_ROLE)} role. Ask an organization admin to assign it, then reload this page.`}
            action={
              <Link to="/transfers" className={buttonVariants({ variant: "secondary" })}>
                Back to transfers
              </Link>
            }
          />
        </div>
      )}
      <Stepper variant="card" steps={WIZARD_STEPS} current={step - 1} className="mb-6" />

      <div className="rounded-lg border border-line bg-surface p-6">
        {step === 1 && (
          <div>
            <h2 className="mb-4 text-[17px] font-bold text-ink">Select Source Wallet</h2>
            {transferData.walletsLoading && (
              <div className="py-8 text-center text-sm text-muted">Loading wallets…</div>
            )}
            {!transferData.walletsLoading && transferData.walletsError && (
              <InfoBanner variant="danger" className="mb-0">
                Could not load wallets ({transferData.walletsError}). Refresh the page or check your access.
              </InfoBanner>
            )}
            {!transferData.walletsLoading && !transferData.walletsError && sourceWallets.length === 0 && (
              <div className="rounded-md bg-surface-muted px-3.5 py-4 text-[13px] text-text">
                No wallets yet. <Link to="/wallets/new" className="font-semibold text-primary underline">Create a wallet</Link> before starting a transfer.
              </div>
            )}
            {!transferData.walletsLoading && sourceWallets.length > 0 && (
              <div className="mb-3.5">
                <Input
                  type="search"
                  data-testid="transfer-wallet-search"
                  aria-label="Search wallets"
                  placeholder="Search by name, address, chain or wallet ID"
                  value={walletQuery}
                  onChange={(e) => setWalletQuery(e.target.value)}
                  className="mb-2.5"
                />
                {hiddenWalletCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowLabWallets((shown) => !shown)}
                    aria-expanded={showLabWallets}
                    className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
                  >
                    <ChevronDown
                      aria-hidden
                      className={cn("size-3.5 transition-transform", showLabWallets && "rotate-180")}
                    />
                    {showLabWallets ? "Hide" : "Show"} test and lab wallets ({hiddenWalletCount})
                  </button>
                )}
              </div>
            )}

            {requestedSourceId && (requestedSourceState === "not_found" || requestedSourceState === "error") && (
              <div data-testid="transfer-source-link-missing">
                <InfoBanner variant="warning" className="mb-3.5">
                  {requestedSourceState === "not_found"
                    ? "The wallet in this link is not one you can send from. Choose a source wallet below."
                    : `Could not load the wallet in this link${lookupFailureText(requestedSourceId ? transferData.walletLookupErrors[requestedSourceId] : undefined)}. Choose a source wallet below, or reload to try again.`}
                </InfoBanner>
              </div>
            )}

            {!transferData.walletsLoading && !transferData.walletsError && typedWalletId && !typedWalletLoaded && (
              <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="transfer-wallet-lookup"
                  disabled={idLookup?.id === typedWalletId && idLookup.state === "looking"}
                  onClick={lookUpTypedWallet}
                >
                  {idLookup?.id === typedWalletId && idLookup.state === "looking" ? "Finding wallet…" : "Find wallet by ID"}
                </Button>
                {idLookup?.id === typedWalletId && idLookup.state === "not_found" && (
                  <span className="text-xs text-muted">No wallet you can send from has this ID.</span>
                )}
                {idLookup?.id === typedWalletId && idLookup.state === "error" && (
                  <span className="text-xs text-danger">Could not look up this wallet{lookupFailureText(transferData.walletLookupErrors[idLookup.id])}. Try again.</span>
                )}
              </div>
            )}

            {!transferData.walletsLoading && sourceWallets.length > 0 && shownSourceWallets.length === 0 && (
              <div className="rounded-md bg-surface-muted px-3.5 py-4 text-[13px] text-text">
                {walletQuery.trim()
                  ? transferData.walletsHasMore
                    ? "No loaded wallets match. Load more wallets, or search by wallet ID."
                    : "No wallets match."
                  : "No named wallets to show. Turn on “Show test and lab wallets”."}
              </div>
            )}

            {!transferData.walletsLoading && shownSourceWallets.length > 0 && (
              <div className="grid gap-2.5 sm:grid-cols-2">
                {shownSourceWallets.map((w) => (
                  <WalletCard
                    key={w.id}
                    walletId={w.id}
                    name={w.name}
                    meta={w.disabled ? (w.frozenLabel ?? walletOptionMeta(w)) : walletOptionMeta(w)}
                    icon={w.name.charAt(0).toUpperCase()}
                    selected={values.sourceWalletId === w.id}
                    disabled={w.disabled}
                    frozen={w.disabled ? "Frozen" : undefined}
                    onClick={() => {
                      if (w.disabled) return;
                      selectSource(w.id);
                    }}
                  />
                ))}
              </div>
            )}

            {/* ALL-6339: WalletKit answers at most 100 wallets per call. Further pages load one at a
                time when the person asks; search and the lab-wallet count cover the loaded wallets. */}
            {!transferData.walletsLoading && !transferData.walletsError && transferData.walletsHasMore && (
              <div className="mt-3.5 flex flex-col items-start gap-2">
                <p className="text-xs text-muted">
                  Showing the first {sourceWallets.length} wallets. Search covers the wallets loaded so far; a wallet ID finds any wallet.
                </p>
                <Button
                  variant="secondary"
                  data-testid="transfer-wallet-load-more"
                  disabled={transferData.walletsLoadingMore}
                  onClick={() => transferData.loadMoreWallets()}
                >
                  {transferData.walletsLoadingMore ? "Loading more wallets…" : "Load more wallets"}
                </Button>
              </div>
            )}
            {!transferData.walletsLoading && transferData.walletsLoadMoreError && (
              <InfoBanner variant="danger" className="mb-0 mt-2.5">
                Could not load more wallets ({transferData.walletsLoadMoreError}). The wallets above are still available; try again.
              </InfoBanner>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="mb-5 flex border-b border-line">
              {DEST_TABS.map(({ key, label, unavailableHint }) => {
                const unavailable = Boolean(unavailableHint);
                const selected = !unavailable && values.destTab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={unavailable}
                    aria-disabled={unavailable || undefined}
                    title={unavailableHint}
                    onClick={() => {
                      if (unavailable) return;
                      set("destTab", key);
                    }}
                    aria-pressed={selected}
                    className={cn(
                      "rounded-t-md border border-b-0 px-4 py-2 text-[13px] font-semibold transition-colors",
                      selected ? "border-transparent bg-primary text-white" : "border-transparent text-muted",
                      unavailable ? "cursor-not-allowed opacity-60" : "hover:text-ink"
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {values.destTab === "internal" && (
              <div>
                <h3 className="mb-3.5 text-base font-bold text-ink">Internal Wallets</h3>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {destWallets.map((w) => (
                    <WalletCard
                      key={w.id}
                      name={w.name}
                      meta={w.meta}
                      icon={w.name.charAt(0).toUpperCase()}
                      selected={values.destWalletId === w.id}
                      onClick={() => set("destWalletId", w.id)}
                    />
                  ))}
                </div>
                <label htmlFor="ext-address-inline" className="mb-2.5 mt-6 block text-sm font-semibold text-ink">
                  Or enter external address
                </label>
                <Input
                  id="ext-address-inline"
                  value={values.externalAddress}
                  onChange={(e) => set("externalAddress", e.target.value)}
                  placeholder="0x… or ENS name"
                  className="font-mono"
                />
                <p className="mt-2 text-[13px] text-muted">
                  Address will be screened against sanctions and risk lists before submission.
                </p>
              </div>
            )}

            {values.destTab === "external" && (
              <div>
                <h3 className="mb-3.5 text-base font-bold text-ink">External Address</h3>
                <Input
                  value={values.externalAddress}
                  onChange={(e) => set("externalAddress", e.target.value)}
                  placeholder="0x… or ENS name"
                  className="font-mono"
                  aria-label="External address"
                />
                <p className="mt-2 text-[13px] text-muted">
                  Address will be screened against sanctions and risk lists before submission.
                </p>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="mb-4 text-[17px] font-bold text-ink">Available Assets ({source?.name ?? "Wallet"})</h2>
            {transferData.assetsLoading && (
              <div className="py-8 text-center text-sm text-muted">Loading assets…</div>
            )}
            {!transferData.assetsLoading && transferData.assetsStatus === "unavailable" && (
              <div role="alert" data-testid="wallet-balances-unavailable">
                <InfoBanner variant="danger" className="mb-0" title={errorCatalog.categories.service_unavailable.title}>
                  {errorCatalog.categories.service_unavailable.message}
                  <SupportDetails reference={transferData.assetsSupportReference} />
                </InfoBanner>
              </div>
            )}
            {!transferData.assetsLoading && transferData.assetsStatus === "unsupported" && (
              <div data-testid="wallet-balances-unsupported">
                <InfoBanner variant="warning" className="mb-0">
                  On-chain balances are not available for this wallet&apos;s network yet.
                </InfoBanner>
              </div>
            )}
            {!transferData.assetsLoading && transferData.assetsStatus === "ok" && assets.length === 0 && (
              <div className="py-8 text-center text-sm text-muted">No assets found for this wallet.</div>
            )}
            {!transferData.assetsLoading && assets.length > 0 && (
            <div className="mb-3.5 overflow-hidden rounded-lg border border-line bg-surface">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line bg-surface-muted text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                    <th className="w-[30px] px-[18px] py-2.5" />
                    <th className="px-[18px] py-2.5">Asset</th>
                    <th className="px-[18px] py-2.5">Available Balance</th>
                    <th className="px-[18px] py-2.5">Network</th>
                    <th className="px-[18px] py-2.5">Route</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a) => {
                    const selected = values.asset === a.symbol;
                    return (
                      <tr
                        key={a.symbol}
                        onClick={() => set("asset", a.symbol)}
                        aria-selected={selected}
                        className={cn(
                          "cursor-pointer border-b border-line last:border-b-0",
                          selected ? "bg-tint-info" : "hover:bg-tint-info"
                        )}
                      >
                        <td className="px-[18px] py-[11px]">
                          <span
                            className={cn(
                              "inline-block size-4 rounded-full border-2 align-middle",
                              selected ? "border-primary bg-primary" : "border-line-strong"
                            )}
                          />
                        </td>
                        <td className="px-[18px] py-[11px] text-sm font-medium text-ink">
                          {a.symbol} — {a.name}
                        </td>
                        <td className="px-[18px] py-[11px] text-sm text-ink">{a.balance}</td>
                        <td className="px-[18px] py-[11px] text-sm text-text">{a.network}</td>
                        <td className="px-[18px] py-[11px] text-sm text-text">{a.route}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
            {!transferData.assetsLoading && assets.length > 0 && (
              <div className="mb-5 rounded-md bg-surface-muted px-3.5 py-2.5 text-[13px] text-text">
                Only assets with a balance in this wallet are shown.
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="max-w-[500px]">
            <div className="mb-5">
              <label htmlFor="tx-amount" className="mb-2 block text-[13px] font-bold text-ink">
                Amount ({values.asset})
              </label>
              <input
                id="tx-amount"
                value={values.amount}
                onChange={(e) => set("amount", e.target.value)}
                inputMode="decimal"
                className="w-full rounded-md border border-line-strong bg-surface px-3.5 py-3 text-xl font-bold text-ink outline-none transition-colors focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
              />
              <p className="mt-1.5 text-[13px] text-muted">
                Available: {asset?.balance ?? "—"} {asset?.symbol ?? ""}
              </p>
            </div>
            <div className="mb-5">
              <Field label="Memo (optional)" htmlFor="tx-memo">
                <Input id="tx-memo" value={values.memo} onChange={(e) => set("memo", e.target.value)} />
              </Field>
            </div>
            <div className="mb-5">
              <Field
                label="Client Reference"
                htmlFor="tx-ref"
                hint="Your own reference for this transfer (optional)."
              >
                <Input id="tx-ref" value={values.clientRef} onChange={(e) => set("clientRef", e.target.value)} className="font-mono" />
              </Field>
            </div>
          </div>
        )}

        {step === 5 && source && asset && (
          <div>
            <div className="mb-6 rounded-lg border border-line bg-surface px-[18px] py-1.5">
              <ReviewRow k="Source" v={`${source.name} (${source.id})`} />
              <ReviewRow k="Destination" v={destinationLabel} />
              <ReviewRow k="Asset / Network" v={`${asset.symbol} · ${asset.network}`} />
              <ReviewRow k="Amount" v={`${values.amount} ${asset.symbol}`} mono />
              <ReviewRow k="Memo" v={values.memo || "—"} />
              <ReviewRow k="Client Reference" v={values.clientRef || "—"} mono />
            </div>

            <h3 className="mb-3 text-[15px] font-bold text-ink">Policy Evaluation</h3>
            {transferData.estimateLoading && (
              <div className="mb-2.5 rounded-lg border border-line bg-surface px-4 py-3 text-[13px] text-muted">
                Evaluating policy…
              </div>
            )}
            {!transferData.estimateLoading && transferData.estimateError && (
              <InfoBanner variant="danger" className="mb-2.5">
                Policy evaluation is unavailable right now ({transferData.estimateError}). You can still submit — the
                backend evaluates policy again at submission time.
              </InfoBanner>
            )}
            {!transferData.estimateLoading && !transferData.estimateError && transferData.estimate && (
              <div className="mb-2.5 rounded-lg border border-tint-success-border-alt bg-tint-success px-4 py-3 text-[13px] text-text-strong">
                {transferData.estimate.requiresApproval === false
                  ? "No approval required for this transfer."
                  : transferData.estimate.requiresApproval === true
                    ? transferData.estimate.approvalPolicy
                      ? `Approval required under ${transferData.estimate.approvalPolicy}.`
                      : "This transfer requires approval before funds move."
                    : transferData.estimate.approvalPolicy
                      ? `Whether approval is needed is decided by ${transferData.estimate.approvalPolicy}.`
                      : "Whether approval is needed is decided by policy when the transfer is submitted."}
                {transferData.estimate.warnings.length > 0 && (
                  <ul className="mt-2 list-disc pl-4">
                    {transferData.estimate.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                )}
              </div>
            )}

            <h3 className="mb-3 mt-5 text-[15px] font-bold text-ink">Approval Route</h3>
            <div className="mb-2.5 rounded-lg border border-line bg-surface px-4 py-3 text-[13px] text-muted">
              Approvers are assigned by policy once this intent is submitted. View the assigned approver on the
              transfer&rsquo;s approval record after submission.
            </div>

            <h3 className="mb-3 mt-5 text-[15px] font-bold text-ink">Fee Estimate</h3>
            <div className="max-w-[340px] rounded-lg border border-line bg-surface px-[18px] py-1.5">
              {transferData.estimateLoading ? (
                <div className="py-4 text-center text-sm text-muted">Estimating fees…</div>
              ) : transferData.estimate ? (
                <>
                  <div className="flex justify-between border-b border-line py-2.5 font-mono text-[13px]">
                    <span className="font-sans text-muted">Network Fee (gas)</span>
                    <span className="font-semibold text-ink">{transferData.estimate.networkFee}</span>
                  </div>
                  <div className="flex justify-between border-b border-line py-2.5 font-mono text-[13px]">
                    <span className="font-sans text-muted">Provider Fee</span>
                    <span className="font-semibold text-ink">{transferData.estimate.providerFee}</span>
                  </div>
                  <div className="flex justify-between py-2.5 font-mono text-[13px]">
                    <span className="font-sans text-muted">Total Estimated Fee</span>
                    <span className="font-semibold text-ink">{transferData.estimate.totalFee}</span>
                  </div>
                </>
              ) : (
                <div className="py-4 text-center text-sm text-muted">Not available.</div>
              )}
            </div>

            {submitError && (
              <InfoBanner variant="danger" className="mt-5 mb-0">
                {submitError}
                <SupportDetails reference={submitErrorSupport.supportReference} capability={submitErrorSupport.capability} />
              </InfoBanner>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {step === 1 ? (
            <Link to="/transfers" className={buttonVariants({ variant: "secondary" })}>
              Cancel
            </Link>
          ) : (
            <Button variant="secondary" onClick={() => go((step - 1) as TransferWizardStep)}>
              ← Back
            </Button>
          )}
          {step < 5 ? (
            <Button variant="primary" disabled={!canContinue || !canCreate} onClick={() => canCreate && next()}>
              {step === 1
                ? "Next: Destination →"
                : step === 2
                  ? "Next: Asset →"
                  : step === 3
                    ? "Next: Amount →"
                    : "Next: Review →"}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!canCreate || submitting || !source || !asset}
              onClick={() => canCreate && setConfirmOpen(true)}
            >
              {submitting ? "Submitting…" : "Submit Intent for Approval"}
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Submit transfer intent for approval?"
        description="This writes an immutable audit record and notifies the assigned approvers."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canCreate || submitting || !source || !asset} onClick={submit}>
              {submitting ? "Submitting…" : "Confirm & Submit"}
            </Button>
          </>
        }
      >
        {source && asset && (
          <div className="overflow-hidden rounded-lg border border-line">
            {[
              { k: "Source", v: `${source.name} (${source.id})` },
              { k: "Destination", v: destinationLabel },
              { k: "Asset / Network", v: `${asset.symbol} · ${asset.network}` },
              { k: "Amount", v: `${values.amount} ${asset.symbol}` },
            ].map((row) => (
              <div key={row.k} className="flex items-center justify-between gap-4 border-b border-line px-4 py-2.5 text-xs last:border-b-0">
                <span className="shrink-0 text-muted">{row.k}</span>
                <span className="text-right font-semibold text-ink">{row.v}</span>
              </div>
            ))}
          </div>
        )}
        <InfoBanner variant="warning" className="mt-4 mb-0">
          Submitting for approval does not move funds. Signing and broadcast occur only after approval is granted.
        </InfoBanner>
      </Modal>
    </div>
  );
}
